import { DriverAuthenticationManager } from '@frontend/authentication-v2';
import { AllowanceClient } from '@frontend/budget/api';
import { BudgetType, UserAllowance } from '@frontend/budget/types';
import { CertificationClient } from '@frontend/certificate/api';
import { UserCertification } from '@frontend/certificate/types';
import { EntityType } from '@frontend/common';
import { Feature } from '@frontend/functionality/types';
import { IGroup, NodeType } from '@frontend/group/types';
import { ProductConsumeClient, ProductOrderClient } from '@frontend/product/api';
import { IProduct, ProductType } from '@frontend/product/types';
import { useDocumentRepository } from '@frontend/repository';
import { SettingKey } from '@frontend/setting/types';
import { StockAvailabilityClient } from '@frontend/stock/api';
import { ProductAvailability } from '@frontend/stock/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useMemo, useState } from 'react';

import { useWizardBasketItems } from '../../hooks/useWizardBasketItems';
import { canOrderProducts } from '../../utils/product-order-permission';
import { useScreen } from '../useScreen';

// Helper functions to distinguish between products and categories
export function isProductCategory(p: IProduct | IGroup): p is IGroup {
    return !!(p as IGroup).node_type;
}

export function isProduct(p: IProduct | IGroup): p is IProduct {
    return !isProductCategory(p);
}

const mergeCatalogues = (consumeLendElements: (IProduct | IGroup)[], orderElements: (IProduct | IGroup)[]): (IProduct | IGroup)[] => {
    const elements = new Map(consumeLendElements.map((element) => [element.id, element]));
    orderElements.forEach((element) => {
        if (isProductCategory(element) || element.type === ProductType.CONSUMABLE) elements.set(element.id, element);
    });
    return Array.from(elements.values());
};

export const useBrowseProductsByCategories = () => {
    const { navigationState, updateWizardData, navigate, store, wizardData, onSubmit } = useScreen();
    const { basketItems } = useWizardBasketItems();
    const { userOverwrite } = wizardData;
    const isRecycleMode = wizardData.browseProductsContext?.feature === Feature.RECYCLE_TRANSACTION;
    const documentRepository = useDocumentRepository();
    const [elements, setElements] = useState<(IProduct | IGroup)[]>([]);

    // Initialize category stack from wizard store
    const [categoryStack, setCategoryStack] = useState<IGroup[]>(() => {
        return store?.data?.browseProductsNavigation?.categoryStack ?? [];
    });
    const [allowances, setAllowances] = useState<UserAllowance | null>(null);
    const [categoryAllowances, setCategoryAllowances] = useState<UserAllowance | null>(null);
    const [certifications, setCertifications] = useState<UserCertification | null>(null);
    const [stockAvailability, setStockAvailability] = useState<ProductAvailability | null>(null);
    const [currentSpotProductAvailability, setCurrentSpotProductAvailability] = useState<ProductAvailability | null>(null);
    const [categoryAvailability, setCategoryAvailability] = useState<ProductAvailability | null>(null);
    const [isLoadingElements, setIsLoadingElements] = useState<boolean>(true);
    const [isLoadingAllowances, setIsLoadingAllowances] = useState<boolean>(false);
    const [isLoadingCategoryAllowances, setIsLoadingCategoryAllowances] = useState<boolean>(false);
    const [isLoadingCertifications, setIsLoadingCertifications] = useState<boolean>(false);
    const [isLoadingStock, setIsLoadingStock] = useState<boolean>(false);
    const [isLoadingCategoryAvailability, setIsLoadingCategoryAvailability] = useState<boolean>(false);
    const [canOrderProductsFromAccount, setCanOrderProductsFromAccount] = useState<boolean | null>(() =>
        isRecycleMode ? false : typeof store?.data?.canOrderProducts === 'boolean' ? store.data.canOrderProducts : null
    );
    const [orderableProductIds, setOrderableProductIds] = useState<Set<string>>(new Set());

    const isLoading =
        isLoadingElements ||
        isLoadingAllowances ||
        isLoadingCategoryAllowances ||
        isLoadingStock ||
        isLoadingCategoryAvailability ||
        canOrderProductsFromAccount === null;

    const rootCategoryId = useMemo(() => {
        if (!navigationState.spot) return null;
        return navigationState.spot.settings[SettingKey.DEFAULT_PRODUCT_ROOT_CATEGORY_ID] || null;
    }, [navigationState.spot]);

    const currentCategoryId = useMemo(() => {
        if (categoryStack.length > 0) {
            return categoryStack[categoryStack.length - 1].id;
        }
        return rootCategoryId;
    }, [categoryStack, rootCategoryId]);

    const currentCategory = useMemo(() => {
        if (categoryStack.length > 0) {
            return categoryStack[categoryStack.length - 1];
        }
        return null;
    }, [categoryStack]);

    const categories = useMemo(() => elements.filter((el): el is IGroup => isProductCategory(el)), [elements]);

    // Separate categories into branch and leaf categories
    const branchCategories = useMemo(() => categories.filter((c) => c.node_type === NodeType.BRANCH), [categories]);
    const leafCategories = useMemo(() => categories.filter((c) => c.node_type === NodeType.LEAF), [categories]);

    const productElements = useMemo(() => elements.filter((el): el is IProduct => isProduct(el)), [elements]);
    const products = useMemo(() => productElements.filter((product) => product.type !== ProductType.KIT), [productElements]);

    const productKits = useMemo(
        () => (isRecycleMode ? [] : productElements.filter((product) => product.type === ProductType.KIT)),
        [productElements, isRecycleMode]
    );

    // Compute stock availability for current spot for each product
    const currentSpotStockAvailability = useMemo(() => {
        if (!currentSpotProductAvailability) return null;
        if (!navigationState.spot?.id) return currentSpotProductAvailability;

        const currentSpotId = navigationState.spot.id;
        const result: ProductAvailability = {};

        Object.entries(currentSpotProductAvailability).forEach(([productId, availability]) => {
            const currentSpotStocks = availability.stocks?.filter((s) => s.spot_id === currentSpotId) ?? [];

            result[productId] = {
                ...availability,
                stocks: currentSpotStocks
            };
        });

        return result;
    }, [currentSpotProductAvailability, navigationState.spot?.id]);

    // Check if products have stock in other spots (for "available elsewhere" message)
    const otherSpotsStockInfo = useMemo(() => {
        if (!stockAvailability || !navigationState.spot?.id) return null;

        const currentSpotId = navigationState.spot.id;
        const result: Record<string, boolean> = {};

        Object.entries(stockAvailability).forEach(([productId, availability]) => {
            const currentSpotAvailability = currentSpotProductAvailability?.[productId];
            if (availability.kit_product) {
                result[productId] = !currentSpotAvailability?.available && availability.available;
                return;
            }

            const otherSpotStocks = availability.stocks?.filter((s) => s.spot_id !== currentSpotId) ?? [];
            const otherSpotQuantity = otherSpotStocks.reduce((sum, s) => sum + s.quantity, 0);
            result[productId] = otherSpotQuantity > 0;
        });

        return result;
    }, [stockAvailability, currentSpotProductAvailability, navigationState.spot?.id]);

    useEffect(() => {
        if (isRecycleMode) return;
        if (canOrderProductsFromAccount !== null || !navigationState.user) return;

        canOrderProducts(navigationState.user.auth).then((canOrder) => {
            setCanOrderProductsFromAccount(canOrder);
            updateWizardData({ canOrderProducts: canOrder });
        });
    }, [canOrderProductsFromAccount, isRecycleMode, navigationState.user, updateWizardData]);

    useEffect(() => {
        if (!currentCategoryId || !navigationState.user || canOrderProductsFromAccount === null) return;

        setIsLoadingElements(true);
        setIsLoadingAllowances(false);
        setIsLoadingCategoryAllowances(false);
        setIsLoadingCertifications(false);
        setIsLoadingStock(false);
        setIsLoadingCategoryAvailability(false);
        setAllowances(null);
        setCategoryAllowances(null);
        setCertifications(null);
        setStockAvailability(null);
        setCurrentSpotProductAvailability(null);
        setCategoryAvailability(null);
        setOrderableProductIds(new Set());

        const spotId = navigationState.spot?.id;
        const accountId = navigationState.user.accountId;

        Promise.all([
            ProductConsumeClient.fetchCategoryConsumeLendProducts(
                accountId,
                currentCategoryId,
                { sort_field: 'order', sort_direction: 'asc' },
                navigationState.user.auth
            ),
            canOrderProductsFromAccount
                ? ProductOrderClient.fetchCategoryOrderProducts(
                      accountId,
                      currentCategoryId,
                      { sort_field: 'order', sort_direction: 'asc' },
                      navigationState.user.auth
                  )
                : Promise.resolve({ results: [] as (IProduct | IGroup)[] })
        ]).then(([consumeLendResponse, orderResponse]) => {
            const catalogueElements = mergeCatalogues(consumeLendResponse.results, orderResponse.results);
            const productItems = catalogueElements.filter((el): el is IProduct => isProduct(el));
            const categoryItems = catalogueElements.filter((el): el is IGroup => isProductCategory(el));
            const productIds = productItems.map((p) => p.id);
            const equipmentProductIds = productItems.filter((product) => product.type === ProductType.EQUIPMENT).map((product) => product.id);
            const leafCategoryIds = categoryItems.filter((c) => c.node_type === NodeType.LEAF).map((c) => c.id);
            setOrderableProductIds(
                new Set(
                    orderResponse.results
                        .filter((element): element is IProduct => isProduct(element) && element.type === ProductType.CONSUMABLE)
                        .map((product) => product.id)
                )
            );

            // Track which additional data needs to be loaded
            const hasProducts = productIds.length > 0;
            const hasLeafCategories = leafCategoryIds.length > 0;

            // Set loading states for additional data
            if (!isRecycleMode && spotId && hasProducts) {
                setIsLoadingAllowances(true);
            }
            if (!isRecycleMode && spotId && hasLeafCategories) {
                setIsLoadingCategoryAllowances(true);
            }
            if (!isRecycleMode && equipmentProductIds.length > 0) {
                setIsLoadingCertifications(true);
            }
            if (!isRecycleMode && hasProducts) {
                setIsLoadingStock(true);
            }
            if (!isRecycleMode && hasLeafCategories) {
                setIsLoadingCategoryAvailability(true);
            }

            // Preload images
            Promise.all([
                documentRepository.preLoadFiles(EntityType.PRODUCT, productIds),
                documentRepository.preLoadFiles(
                    EntityType.PRODUCT_CATEGORY,
                    categoryItems.map((c) => c.id)
                )
            ]).then(() => {
                setElements(catalogueElements);
                setIsLoadingElements(false);
            });

            // Fetch allowances for products
            if (!isRecycleMode && spotId && hasProducts) {
                AllowanceClient.postGetUserAllowancesV2(
                    productIds,
                    {
                        spot_id: spotId,
                        user_id: userOverwrite?.id ?? undefined,
                        type: BudgetType.CONSUMPTION
                    },
                    navigationState.user!.auth
                )
                    .then(setAllowances)
                    .catch(() => setAllowances({}))
                    .finally(() => setIsLoadingAllowances(false));
            }

            // Fetch allowances for leaf categories
            if (!isRecycleMode && spotId && hasLeafCategories) {
                AllowanceClient.postGetUserCategoryAllowances2(
                    leafCategoryIds,
                    { spot_id: spotId, user_id: userOverwrite?.id ?? undefined },
                    navigationState.user!.auth
                )
                    .then(setCategoryAllowances)
                    .catch(() => setCategoryAllowances({}))
                    .finally(() => setIsLoadingCategoryAllowances(false));
            }

            if (!isRecycleMode && equipmentProductIds.length > 0) {
                DriverAuthenticationManager.getInstance()
                    .waitForToken()
                    .then((iotToken) =>
                        CertificationClient.postGetProductCertification(accountId, userOverwrite?.id ?? navigationState.user!.id, equipmentProductIds, iotToken)
                    )
                    .then(setCertifications)
                    .catch(() => setCertifications({}))
                    .finally(() => setIsLoadingCertifications(false));
            }

            // Fetch stock availability for products (all spots - we'll filter in UI)
            if (!isRecycleMode && hasProducts) {
                Promise.all([
                    StockAvailabilityClient.postGetProductAvailability(accountId, productIds),
                    spotId ? StockAvailabilityClient.postGetProductAvailability(accountId, productIds, spotId) : Promise.resolve(null)
                ])
                    .then(([allSpotAvailability, currentSpotAvailability]) => {
                        setStockAvailability(allSpotAvailability);
                        setCurrentSpotProductAvailability(currentSpotAvailability ?? allSpotAvailability);
                    })
                    .finally(() => setIsLoadingStock(false));
            }

            // Fetch category availability for leaf categories (all spots)
            if (!isRecycleMode && hasLeafCategories) {
                StockAvailabilityClient.postGetProductCategoryAvailability(accountId, leafCategoryIds)
                    .then(setCategoryAvailability)
                    .finally(() => setIsLoadingCategoryAvailability(false));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canOrderProductsFromAccount, currentCategoryId, navigationState.spot?.id, navigationState.user?.id, userOverwrite?.id, isRecycleMode]);

    const navigateToCategory = (category: IGroup) => {
        // Check if this is a leaf category
        if (category.node_type === NodeType.LEAF) {
            // Persist the current stack (without the leaf) so going back from
            // SELECT_ATTRIBUTES restores us to the leaf's parent category, not
            // the leaf itself. The leaf is tracked separately via
            // `selectedLeafCategory`.
            updateWizardData({
                selectedLeafCategory: category,
                browseProductsNavigation: {
                    categoryStack,
                    leafSelectedProduct: null,
                    selectedProductStocks: []
                }
            });
            navigate(`../${ScreenType.SELECT_ATTRIBUTES}`);
        } else {
            // For branch categories, just push to stack
            setCategoryStack((prev) => [...prev, category]);
        }
    };

    const navigateBack = () => {
        setCategoryStack((prev) => prev.slice(0, -1));
    };

    const onProductClick = (product: IProduct) => {
        // Store the selected product and navigate to the appropriate screen
        updateWizardData({
            browseProductsNavigation: {
                categoryStack,
                leafSelectedProduct: null,
                selectedProductStocks: []
            },
            selectedProduct: product,
            selectedProductCanBeOrdered: orderableProductIds.has(product.id),
            selectedCertification: isRecycleMode || product.type !== ProductType.EQUIPMENT ? null : (certifications?.[product.id] ?? null),
            selectedCostCenterId: null,
            selectedCostCenterName: null
        });
        let nextScreen = ScreenType.SELECT_QUANTITY;
        if (isRecycleMode) nextScreen = ScreenType.CREATE_RECYCLE_TRANSACTION;
        else if (product.type === ProductType.KIT) {
            const continueScreen = store?.steps?.find(
                (step) => step.screen === ScreenType.BROWSE_PRODUCTS_BY_CATEGORIES && step.trigger === ScreenTrigger.SUBMIT
            );
            onSubmit(continueScreen);
            return;
        }

        navigate(`../${nextScreen}`);
    };

    const canGoBack = categoryStack.length > 0;
    const navigateToBasket = () => navigate(`../${ScreenType.BASKET_OVERVIEW}`);

    return {
        categories,
        branchCategories,
        leafCategories,
        products,
        productKits,
        documents: documentRepository,
        navigateToCategory,
        navigateBack,
        canGoBack,
        categoryStack,
        currentCategory,
        onProductClick,
        spot: navigationState.spot,
        allowances,
        categoryAllowances,
        stockAvailability: currentSpotStockAvailability,
        otherSpotsStockInfo,
        certifications,
        isLoadingCertifications,
        categoryAvailability,
        isLoading,
        isRecycleMode,
        basketItemCount: basketItems.length,
        navigateToBasket,
        hasOrderPermission: canOrderProductsFromAccount === true,
        canOrderOutOfStock: (product: IProduct) => product.type === ProductType.CONSUMABLE && orderableProductIds.has(product.id)
    };
};
